/**
 * Shared field configuration for data sources.
 * Used by both DataSource (add) and DataConnections (edit) components.
 */

const dbFields = [
  {
    name: "server",
    label: "Server",
    type: "text",
    required: false,
  },
  {
    name: "database",
    label: "Database",
    type: "text",
    required: true,
  },
  {
    name: "username",
    label: "User Name",
    type: "text",
    required: true,
  },
  {
    name: "password",
    label: "Password",
    type: "password",
    required: true,
  },
  {
    name: "host",
    label: "Host",
    type: "text",
    required: true,
  },
  {
    name: "port",
    label: "Port",
    type: "number",
    required: false,
  },
  {
    name: "corporationDbName",
    label: "Connection Name",
    type: "text",
    required: true,
  },
  {
    name: "description",
    label: "Description",
    type: "textarea",
    required: false,
  },
];

const fileUploadField = [
  { name: "file", label: "Upload File", type: "file", required: true, multiple: true },
  { name: "corporationDbName", label: "Connection Name", type: "text", required: true },
  { name: "storeFile", type: "checkbox", required: true },
];

export const fieldConfig = {
  json: [{ name: "url", label: "JSON URL", type: "text" }],
  xml: fileUploadField,
  excel_csv: fileUploadField,
  csv: fileUploadField,
  api: [
    { name: "endpoint", label: "API Endpoint", type: "text", required: true },
    { name: "headers", label: "Headers", type: "textarea" },
  ],
  postgresql: dbFields,
  mysql: dbFields,
  sqlserver: dbFields,
  microsoft_fabric: dbFields,
  CrateDb: dbFields,
  db2: dbFields,
  exact: dbFields,
  infor: dbFields,
  sage: dbFields,
odoo: dbFields.map((field) =>
  field.name === "host"
    ? { ...field, name: "baseUrl", label: "Base URL", required: true }
    : field
),
  sugarcrm: dbFields,
  ms_dynamics_ax: dbFields,
  ms_dynamics_crm: dbFields,
  ms_dynamics_nav: dbFields,
  jdedwards: dbFields,
  bullhorn: dbFields,
  sap_hana: [
    {
      name: "server",
      label: "Server",
      type: "text",
      required: true,
    },
    {
      name: "port",
      label: "Port",
      type: "number",
      required: true,
    },
    {
      name: "username",
      label: "User Name",
      type: "text",
      required: true,
    },
    {
      name: "password",
      label: "Password",
      type: "password",
      required: true,
    },
    {
      name: "corporationDbName",
      label: "Connection Name",
      type: "text",
      required: true,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
    },
  ],
  sap_c4c: dbFields,
  salesforce: [
    { name: "baseURL", label: "Base URL", type: "text", required: true },
    { name: "clientId", label: "Consumer Key", type: "text", required: true },
    { name: "clientSecret", label: "Consumer Secret", type: "text", required: true },
    { name: "securityToken", label: "Security Token", type: "text", required: true },
    { name: "username", label: "Username", type: "text", required: true },
    { name: "password", label: "Password", type: "password", required: true },
    { name: "apiVersion", label: "API Version", type: "text", required: true },
    { name: "CorporationDbName", label: "Connection Name", type: "text", required: true },
  ],
};

/**
 * Maps API/DB dataSourceType to fieldConfig key.
 * DB table names: SalesForce, MySql, Infor, Fabric, Db2, MsSql, Xml, Json, Odoo,
 * CrateDb, PostgresSQL, Excel, API, CSV, Microsoft Dynamics Ax2012, S4HANA, etc.
 */
export const dataSourceTypeToConfigKey = {
  // PostgreSQL
  postgresql: "postgresql",
  postgressql: "postgresql",
  PostgresSQL: "postgresql",
  postgres: "postgresql",
  // SQL Server
  mssql: "sqlserver",
  msql: "sqlserver",
  MsSql: "sqlserver",
  sqlserver: "sqlserver",
  "sql server": "sqlserver",
  // Excel / CSV
  excel: "excel_csv",
  Excel: "excel_csv",
  "excel/csv": "excel_csv",
  excel_csv: "excel_csv",
  CSV: "csv",
  csv: "csv",
  // MySQL
  mysql: "mysql",
  MySql: "mysql",
  // CrateDb
  cratedb: "CrateDb",
  CrateDb: "CrateDb",
  // DB2
  db2: "db2",
  Db2: "db2",
  "ibm db2": "db2",
  // SAP
  "sap hana": "sap_hana",
  hana: "sap_hana",
  s4hana: "sap_hana",
  S4HANA: "sap_hana",
  "SAP_HANA": "sap_hana",
  sap_c4c: "sap_c4c",
  "sap c4c": "sap_c4c",
  // Fabric
  fabric: "microsoft_fabric",
  Fabric: "microsoft_fabric",
  microsoft_fabric: "microsoft_fabric",
  "ms fabric": "microsoft_fabric",
  // Salesforce
  salesforce: "salesforce",
  SalesForce: "salesforce",
  // JSON / XML / API
  json: "json",
  Json: "json",
  xml: "xml",
  Xml: "xml",
  api: "api",
  API: "api",
  // ERPs / CRMs
  exact: "exact",
  Exact: "exact",
  sage: "sage",
  Sage: "sage",
  odoo: "odoo",
  Odoo: "odoo",
  sugarcrm: "sugarcrm",
  SugarCRM: "sugarcrm",
  infor: "infor",
  Infor: "infor",
  bullhorn: "bullhorn",
  Bullhorn: "bullhorn",
  // MS Dynamics
  "Microsoft Dynamics Ax2012": "ms_dynamics_ax",
  "microsoft dynamics ax": "ms_dynamics_ax",
  "microsoft_dynamics_ax2012": "ms_dynamics_ax",
  "ms dynamics ax": "ms_dynamics_ax",
  DynamicsAX: "ms_dynamics_ax",
  ms_dynamics_ax: "ms_dynamics_ax",
  "Microsoft Dynamics CRM": "ms_dynamics_crm",
  "microsoft dynamics crm": "ms_dynamics_crm",
  DynamicsCRM: "ms_dynamics_crm",
  ms_dynamics_crm: "ms_dynamics_crm",
  "Microsoft Dynamics NAV": "ms_dynamics_nav",
  "microsoft dynamics nav": "ms_dynamics_nav",
  DynamicsNAV: "ms_dynamics_nav",
  ms_dynamics_nav: "ms_dynamics_nav",
  // JD Edwards
  "jd edwards": "jdedwards",
  jdedwards: "jdedwards",
  JDEdwards: "jdedwards",
};

/**
 * Get fieldConfig key from connection's dataSourceType
 */
export const getFieldConfigKey = (dataSourceType) => {
  if (!dataSourceType) return null;
  const str = String(dataSourceType).trim();
  const normalized = str.toLowerCase().replaceAll(/\s+/g, "_");
  // Try exact match first (PostgresSQL, MsSql, S4HANA, etc.)
  return (
    dataSourceTypeToConfigKey[str] ||
    dataSourceTypeToConfigKey[normalized] ||
    dataSourceTypeToConfigKey[str.toLowerCase()] ||
    null
  );
};

export default fieldConfig;
